import * as React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Plus, Search, Map, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { trpc } from "@/lib/trpc";
import { useRole } from "@/hooks/useRole";
import { KanbanBoard } from "./components/roadmap/KanbanBoard";
import { FeatureFormModal } from "./components/roadmap/FeatureFormModal";
import { FeatureDetailModal } from "./components/roadmap/FeatureDetailModal";
import { DeleteConfirmDialog } from "./components/roadmap/DeleteConfirmDialog";

export function RoadmapPage() {
  const { t } = useTranslation("dashboard");
  const { isAdmin, role } = useRole();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedFeatureId, setSelectedFeatureId] = useState<string | null>(null);

  // Redirect non-admins
  useEffect(() => {
    if (role !== null && !isAdmin) {
      navigate({ to: "/dashboard" });
    }
  }, [role, isAdmin, navigate]);

  const { data: features = [], isLoading } = trpc.feature.list.useQuery();
  const utils = trpc.useUtils();

  const deleteMutation = trpc.feature.delete.useMutation({
    onSuccess: () => {
      utils.feature.list.invalidate();
      setDeleteDialogOpen(false);
      setDetailModalOpen(false);
      setSelectedFeatureId(null);
    },
  });

  const selectedFeature = selectedFeatureId
    ? features.find((f) => f.id === selectedFeatureId)
    : null;

  const handleFeatureClick = (id: string) => {
    setSelectedFeatureId(id);
    setDetailModalOpen(true);
  };

  const handleEditFromDetail = () => {
    setDetailModalOpen(false);
    setEditModalOpen(true);
  };

  const handleDeleteFromDetail = () => {
    setDetailModalOpen(false);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedFeatureId) {
      deleteMutation.mutate({ id: selectedFeatureId });
    }
  };

  // Don't render until we know the role
  if (role === null) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t("pages.roadmap.title")}
          </h1>
          <p className="text-base text-muted-foreground md:text-lg">
            {t("pages.roadmap.subtitle")}
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Feature
        </Button>
      </div>

      <Separator />

      {/* Search */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search features..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border-border bg-muted pl-9 text-sm placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary/50"
          />
        </div>
      </div>

      {/* Kanban Board */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : features.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center text-center">
          <Map className="mb-3 h-12 w-12 text-muted-foreground/50" />
          <p className="text-muted-foreground">No features yet</p>
          <p className="mt-1 text-sm text-muted-foreground/60">
            Click "Add Feature" to get started
          </p>
        </div>
      ) : (
        <KanbanBoard
          features={features}
          searchQuery={searchQuery}
          onFeatureClick={handleFeatureClick}
        />
      )}

      {/* Create Modal */}
      <FeatureFormModal open={createModalOpen} onOpenChange={setCreateModalOpen} mode="create" />

      {/* Edit Modal */}
      <FeatureFormModal
        open={editModalOpen}
        onOpenChange={(open) => {
          setEditModalOpen(open);
          if (!open) setSelectedFeatureId(null);
        }}
        mode="edit"
        defaultValues={
          selectedFeature
            ? {
                id: selectedFeature.id,
                name: selectedFeature.name,
                description: selectedFeature.description ?? "",
                status: selectedFeature.status,
                priority: selectedFeature.priority,
              }
            : undefined
        }
      />

      {/* Detail Modal */}
      <FeatureDetailModal
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        featureId={selectedFeatureId}
        onEdit={handleEditFromDetail}
        onDelete={handleDeleteFromDetail}
      />

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        featureName={selectedFeature?.name ?? ""}
        onConfirm={handleConfirmDelete}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}

export default RoadmapPage;
